/* models/idom.js */ function _(require, module, exports, __esModule, __esExport) {
    __esModule();
    const preact_1 = require("6f11f2ef27") /* preact */;
    const hooks_1 = require("4c02a9b43f") /* preact/hooks */;
    const preact_2 = require("b3f51db71c") /* htm/preact */;
    const fast_json_patch_1 = require("cbecfde9cd") /* fast-json-patch */;
    const html_box_1 = require("@bokehjs/models/layouts/html_box");
    const layout_1 = require("80a6464b1f") /* ./layout */;
    const event_to_object_1 = require("d92f7d4d2b") /* ./event-to-object */;
    const LayoutConfigContext = (0, preact_1.createContext)({});
    function mountLayout(mountElement, saveUpdateHook, sendEvent, importSourceUrl) {
        (0, preact_1.render)((0, preact_2.html) `
      <${Layout}
        saveUpdateHook=${saveUpdateHook}
        sendEvent=${sendEvent}
        importSourceUrl=${importSourceUrl}
      />
    `, mountElement);
    }
    exports.mountLayout = mountLayout;
    function Layout({ saveUpdateHook, sendEvent, importSourceUrl }) {
        const [model, patchModel] = useInplaceJsonPatch({});
        (0, hooks_1.useEffect)(() => saveUpdateHook(patchModel), [patchModel]);
        if (model.tagName) {
            return (0, preact_2.html) `
      <${LayoutConfigContext.Provider}
        value=${{
                sendEvent: sendEvent,
                importSourceUrl: importSourceUrl,
            }}
      >
        <${Element} model=${model} />
      <//>
    `;
        }
        else
            return (0, preact_2.html) `<div />`;
    }
    exports.default = Layout;
    function Element({ model }) {
        if (model.importSource)
            return (0, preact_2.html) `<${ImportedElement} model=${model} />`;
        else
            return (0, preact_2.html) `<${StandardElement} model=${model} />`;
    }
    function ImportedElement({ model }) {
        const config = (0, hooks_1.useContext)(LayoutConfigContext);
        const module = useLazyModule(model.importSource.source, config.importSourceUrl);
        if (module) {
            const cmpt = getPathProperty(module, model.tagName);
            const children = elementChildren(model);
            const attributes = elementAttributes(model, config.sendEvent);
            return (0, preact_2.html) `<${cmpt} ...${attributes}>${children}<//>`;
        }
        else {
            const fallback = model.importSource.fallback;
            if (!fallback)
                return (0, preact_2.html) `<div />`;
            switch (typeof fallback) {
                case "object":
                    return (0, preact_2.html) `<${Element} model=${fallback} />`;
                case "string":
                    return (0, preact_2.html) `<div>${fallback}</div>`;
                default:
                    return null;
            }
        }
    }
    function StandardElement({ model }) {
        const config = (0, hooks_1.useContext)(LayoutConfigContext);
        const children = elementChildren(model);
        const attributes = elementAttributes(model, config.sendEvent);
        if (model.children && model.children.length)
            return (0, preact_2.html) `<${model.tagName} ...${attributes}>${children}<//>`;
        else
            return (0, preact_2.html) `<${model.tagName} ...${attributes} />`;
    }
    function elementChildren(model) {
        if (!model.children)
            return [];
        else {
            return model.children.map((child) => {
                switch (typeof child) {
                    case "object":
                        return (0, preact_2.html) `<${Element} model=${child} />`;
                    case "string":
                        return child;
                    default:
                        return null;
                }
            });
        }
    }
    function elementAttributes(model, sendEvent) {
        const attributes = Object.assign({}, model.attributes);
        if (model.eventHandlers) {
            Object.keys(model.eventHandlers).forEach((eventName) => {
                const eventSpec = model.eventHandlers[eventName];
                attributes[eventName] = eventHandler(sendEvent, eventSpec);
            });
        }
        return attributes;
    }
    function eventHandler(sendEvent, eventSpec) {
        return function () {
            const data = Array.from(arguments).map((value) => {
                if (typeof value === "object") {
                    if (eventSpec["preventDefault"])
                        value.preventDefault();
                    if (eventSpec["stopPropagation"])
                        value.stopPropagation();
                    return (0, event_to_object_1.serializeEvent)(value);
                }
                else
                    return value;
            });
            return new Promise((resolve) => {
                const msg = {
                    data: data,
                    target: eventSpec["target"],
                };
                sendEvent(msg);
                resolve(msg);
            });
        };
    }
    function useLazyModule(source, sourceUrlBase = "") {
        const [module, setModule] = (0, hooks_1.useState)(null);
        // use eval() to avoid weird build behavior by bundlers like Webpack
        if (!module)
            eval(`import('${joinUrl(sourceUrlBase, source)}')`).then(setModule);
        return module;
    }
    function getPathProperty(obj, prop) {
        // properties may be dot seperated strings
        const path = prop.split(".");
        const firstProp = path.shift();
        let value = obj[firstProp];
        for (let i = 0; i < path.length; i++)
            value = value[path[i]];
        return value;
    }
    function useInplaceJsonPatch(doc) {
        const ref = (0, hooks_1.useRef)(doc);
        const forceUpdate = useForceUpdate();
        const applyPatch = (0, hooks_1.useCallback)((path, patch) => {
            applyPatchInplace(ref.current, path, patch);
            forceUpdate();
        }, [ref, forceUpdate]);
        return [ref.current, applyPatch];
    }
    function applyPatchInplace(doc, path, patch) {
        if (!path)
            (0, fast_json_patch_1.applyPatch)(doc, patch);
        else {
            (0, fast_json_patch_1.applyPatch)(doc, [
                {
                    op: "replace",
                    path: path,
                    value: (0, fast_json_patch_1.applyPatch)((0, fast_json_patch_1.getValueByPointer)(doc, path), patch, false, false).newDocument,
                },
            ]);
        }
    }
    function useForceUpdate() {
        const [, updateState] = (0, hooks_1.useState)({});
        return (0, hooks_1.useCallback)(() => updateState({}), []);
    }
    function joinUrl(base, tail) {
        return tail.startsWith("./")
            ? (base.endsWith("/") ? base.slice(0, -1) : base) + tail.slice(1)
            : tail;
    }
    class IDOMView extends layout_1.PanelHTMLBoxView {
        connect_signals() {
            super.connect_signals();
            this.connect(this.model.properties.event.change, () => {
                this._update(...this.model.event);
                setTimeout(() => { requestAnimationFrame(() => this.fix_layout()); });
            });
        }
        fix_layout() {
            this.update_layout();
            this.compute_layout();
            this.invalidate_layout();
            (0, layout_1.set_size)(this.el, this.model);
        }
        initialize() {
            super.initialize();
            mountLayout(this.el, (update) => this._save_update(update), (event) => this._send(event), this.model.importSourceUrl);
        }
        async lazy_initialize() {
            await super.lazy_initialize();
            await new Promise((resolve) => {
                const check_update = () => {
                    if (this._update)
                        resolve(null);
                    else
                        setTimeout(check_update, 100);
                };
                check_update();
            });
        }
        _save_update(update) {
            this._update = update;
        }
        async render() {
            super.render();
            this._update(...this.model.event);
            await new Promise((resolve) => {
                const check_update = () => {
                    if (this.el.children.length) {
                        this.fix_layout();
                        resolve(null);
                    }
                    else
                        setTimeout(check_update, 50);
                };
                check_update();
            });
        }
        _send(event) {
            this.model.msg = event;
        }
    }
    exports.IDOMView = IDOMView;
    IDOMView.__name__ = "IDOMView";
    class IDOM extends html_box_1.HTMLBox {
        constructor(attrs) {
            super(attrs);
        }
        static init_IDOM() {
            this.prototype.default_view = IDOMView;
            this.define(({ Any, String }) => ({
                event: [Any, []],
                importSourceUrl: [String, ''],
                msg: [Any, {}],
            }));
        }
    }
    exports.IDOM = IDOM;
    IDOM.__name__ = "IDOM";
    IDOM.__module__ = "panel.models.idom";
    IDOM.init_IDOM();
}
